import type { ThemeToken } from '../store/types';
import { SchemaError } from './serialize';
import { downloadBlob } from './download';

function sanitize(s: string): string {
  return s.replace(/[^a-z0-9._-]+/gi, '-').replace(/^-+|-+$/g, '') || 'theme';
}

export function themeFilename(theme: ThemeToken): string {
  return `${sanitize(theme.name || theme.id)}.theme.json`;
}

export function exportTheme(theme: ThemeToken): void {
  downloadBlob(themeFilename(theme), JSON.stringify(theme, null, 2), 'application/json');
}

function isObj(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function validateModeGroups(m: Record<string, unknown>, where: string): void {
  for (const g of ['colors', 'typography', 'rounded', 'spacing'] as const) {
    if (!isObj(m[g])) throw new SchemaError(`Theme ${where}.${g} must be an object`);
  }
}

export function parseThemeJson(text: string): ThemeToken {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new SchemaError('Invalid JSON');
  }
  if (!isObj(raw)) throw new SchemaError('Theme must be an object');
  if (typeof raw.id !== 'string' || !raw.id) throw new SchemaError('Theme.id is required');
  if (typeof raw.name !== 'string' || !raw.name) throw new SchemaError('Theme.name is required');
  validateModeGroups(raw, 'theme');
  if (raw.dark !== undefined) {
    if (!isObj(raw.dark)) throw new SchemaError('Theme.dark must be an object');
    // dark is a partial set; only validate the groups that are present.
    for (const g of ['colors', 'typography', 'rounded', 'spacing'] as const) {
      if (raw.dark[g] !== undefined && !isObj(raw.dark[g])) {
        throw new SchemaError(`Theme.dark.${g} must be an object`);
      }
    }
  }
  return raw as unknown as ThemeToken;
}
